import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Calendar, GraduationCap, CheckCircle2, Info } from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdmissionSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    status: 'Open',
    session_year: '2025-26',
    start_date: '',
    end_date: '',
    session_start_date: ''
  });

  useEffect(() => {
    const verifyAndFetch = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        // Verify admin
        await axios.get(`${BACKEND_URL}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Fetch current settings
        const response = await axios.get(`${BACKEND_URL}/api/admission-settings`);
        setSettings(response.data);
        
        // Check if auto-closed
        const currentDate = new Date();
        const endDate = new Date(response.data.end_date);
        if (currentDate > endDate && response.data.status === 'Closed') {
          toast.info('Admissions were automatically closed', {
            description: 'The admission end date has passed. You can reopen admissions by changing the dates and status.'
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error:', err);
        if (err.response?.status === 401) {
          localStorage.removeItem('adminToken');
          navigate('/admin/login');
        } else {
          setLoading(false);
        }
      }
    };

    verifyAndFetch();
  }, [navigate]);

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleToggleStatus = () => {
    setSettings({ 
      ...settings, 
      status: settings.status === 'Open' ? 'Closed' : 'Open' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    // Validate dates
    if (new Date(settings.start_date) >= new Date(settings.end_date)) {
      toast.error('Invalid dates', {
        description: 'Start date must be before end date'
      });
      setSaving(false);
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/admin/admission-settings`, settings);
      
      toast.success('Admission settings updated successfully!', {
        description: `Admissions are now ${settings.status} for session ${settings.session_year}`
      });
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to update settings', {
        description: err.response?.data?.detail || 'Please try again'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700 font-medium">Loading Settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={schoolInfo.logo} 
                alt="AMTEE PUBLIC SCHOOL" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admission Settings</h1>
                <p className="text-sm text-gray-600">Control admission status and dates</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/dashboard')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <GraduationCap className="h-6 w-6" />
              Admission Control System
            </CardTitle>
            <CardDescription className="text-blue-100">
              Manage admission status and session dates for the school website
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-8">
            {/* Info Alert */}
            <Alert className="bg-blue-50 border-blue-200 mb-6">
              <Info className="h-5 w-5 text-blue-600" />
              <AlertDescription className="text-blue-800">
                These settings control the "Admission Open" button on the homepage. Changes are reflected immediately.
              </AlertDescription>
            </Alert>

            {/* Auto-Closure Warning */}
            {settings.status === 'Closed' && new Date() > new Date(settings.end_date) && (
              <Alert className="bg-amber-50 border-amber-300 mb-6">
                <Info className="h-5 w-5 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>Auto-Closed:</strong> Admissions were automatically closed because the end date has passed. 
                  To reopen, update the end date to a future date and toggle the status to "Open".
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Admission Status Toggle */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Admission Status *</Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Control whether admissions are currently accepting applications
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    className={`relative inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      settings.status === 'Open' ? 'bg-green-600' : 'bg-red-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform ${
                        settings.status === 'Open' ? 'translate-x-12' : 'translate-x-1'
                      }`}
                    />
                    <span className={`absolute text-xs font-bold text-white ${
                      settings.status === 'Open' ? 'left-2' : 'right-2'
                    }`}>
                      {settings.status === 'Open' ? 'OPEN' : 'CLOSED'}
                    </span>
                  </button>
                </div>
                
                <div className={`mt-4 p-3 rounded-lg ${
                  settings.status === 'Open' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <p className={`text-sm font-semibold ${
                    settings.status === 'Open' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {settings.status === 'Open' 
                      ? '✅ Admission button will be visible on homepage' 
                      : '🚫 Admission button will be hidden or show "Admissions Closed"'}
                  </p>
                </div>
              </div>

              {/* Session Year */}
              <div>
                <Label htmlFor="session_year" className="text-base font-semibold">
                  Academic Session Year *
                </Label>
                <div className="relative mt-2">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="session_year"
                    name="session_year"
                    value={settings.session_year}
                    onChange={handleChange}
                    placeholder="e.g., 2025-26, 2026-27"
                    className="pl-10 py-6"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  This will be displayed on the homepage button and admission forms
                </p>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="start_date" className="text-base font-semibold">
                    Application Start Date *
                  </Label>
                  <Input
                    id="start_date"
                    name="start_date"
                    type="date"
                    value={settings.start_date}
                    onChange={handleChange}
                    className="mt-2 py-6"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    When applications open
                  </p>
                </div>

                <div>
                  <Label htmlFor="end_date" className="text-base font-semibold">
                    Application Last Date *
                  </Label>
                  <Input
                    id="end_date"
                    name="end_date"
                    type="date"
                    value={settings.end_date}
                    onChange={handleChange}
                    className="mt-2 py-6"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    When applications close
                  </p>
                </div>

                <div>
                  <Label htmlFor="session_start_date" className="text-base font-semibold">
                    Session Start Date
                  </Label>
                  <Input
                    id="session_start_date"
                    name="session_start_date"
                    type="date"
                    value={settings.session_start_date || ''}
                    onChange={handleChange}
                    className="mt-2 py-6"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    When academic session begins
                  </p>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-2 border-purple-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="h-5 w-5 text-purple-600" />
                  Homepage Preview
                </h3>
                <div className="bg-white p-4 rounded-lg shadow-inner">
                  {settings.status === 'Open' ? (
                    <div>
                      <Button className="bg-gradient-to-r from-green-600 to-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg">
                        🎓 Admission Open (Session {settings.session_year})
                      </Button>
                      <p className="text-sm text-gray-600 mt-2">
                        Admissions open till {new Date(settings.end_date).toLocaleDateString('en-US', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center p-4 bg-gray-100 rounded-lg">
                      <p className="text-gray-600 font-medium">🚫 Admissions Closed</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/admin/dashboard')}
                  className="flex-1"
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 py-6 text-lg font-semibold"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdmissionSettingsPage;
